import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL } from '../config/api';
import { showToast } from '../utils/toast';
import './Profile.css';

function Profile() {
  const { user } = useAuth();
  const [userDetails, setUserDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [profileData, setProfileData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    date_of_birth: '',
    gender: '',
    height: '',
    weight: '',
    fitness_goals: ''
  });

  useEffect(() => {
    if (user) {
      fetchUserDetails();
    }
  }, [user]);

  const fetchUserDetails = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/users/${user.id}`);
      setUserDetails(response.data);
      setProfileData({
        first_name: response.data.first_name || '',
        last_name: response.data.last_name || '',
        email: response.data.email || '',
        phone: response.data.phone || '',
        date_of_birth: response.data.date_of_birth || '',
        gender: response.data.gender || '',
        height: response.data.height || '',
        weight: response.data.weight || '',
        fitness_goals: response.data.fitness_goals || ''
      });
    } catch (error) {
      console.error('Error fetching user details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_BASE_URL}/api/users/${user.id}/profile`, profileData);
      showToast.success('Profile updated successfully!');
      setEditing(false);
      fetchUserDetails();
    } catch (error) {
      showToast.error('Error updating profile: ' + (error.response?.data?.error || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return null;
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  if (loading) {
    return (
      <div className="profile-container">
        <div className="container">
          <div className="card profile-card">
            <p>Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-container">
      <div className="container">
        <div className="card profile-card">
          <div className="profile-header">
            <h2>User Profile</h2>
            {!editing && (
              <button onClick={() => setEditing(true)} className="btn-primary">
                Edit Profile
              </button>
            )}
          </div>

          {editing ? (
            <div className="profile-edit">
              <div className="profile-section">
                <h3>Personal Information</h3>
                <div className="profile-form-grid">
                  <div className="form-group">
                    <label>First Name</label>
                    <input
                      type="text"
                      name="first_name"
                      value={profileData.first_name}
                      onChange={handleInputChange}
                      placeholder="Enter first name"
                    />
                  </div>
                  <div className="form-group">
                    <label>Last Name</label>
                    <input
                      type="text"
                      name="last_name"
                      value={profileData.last_name}
                      onChange={handleInputChange}
                      placeholder="Enter last name"
                    />
                  </div>
                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      name="email"
                      value={profileData.email}
                      onChange={handleInputChange}
                      placeholder="Enter email"
                    />
                  </div>
                  <div className="form-group">
                    <label>Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      value={profileData.phone}
                      onChange={handleInputChange}
                      placeholder="Enter phone number"
                    />
                  </div>
                  <div className="form-group">
                    <label>Date of Birth</label>
                    <input
                      type="date"
                      name="date_of_birth"
                      value={profileData.date_of_birth}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="form-group">
                    <label>Gender</label>
                    <select
                      name="gender"
                      value={profileData.gender}
                      onChange={handleInputChange}
                    >
                      <option value="">Select gender</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                      <option value="prefer_not_to_say">Prefer not to say</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="profile-section">
                <h3>Fitness Information</h3>
                <div className="profile-form-grid">
                  <div className="form-group">
                    <label>Height (cm)</label>
                    <input
                      type="number"
                      name="height"
                      value={profileData.height}
                      onChange={handleInputChange}
                      placeholder="Enter height in cm"
                      min="0"
                      step="0.1"
                    />
                  </div>
                  <div className="form-group">
                    <label>Weight (kg)</label>
                    <input
                      type="number"
                      name="weight"
                      value={profileData.weight}
                      onChange={handleInputChange}
                      placeholder="Enter weight in kg"
                      min="0"
                      step="0.1"
                    />
                  </div>
                  <div className="form-group full-width">
                    <label>Fitness Goals</label>
                    <textarea
                      name="fitness_goals"
                      value={profileData.fitness_goals}
                      onChange={handleInputChange}
                      placeholder="Describe your fitness goals..."
                      rows="4"
                    />
                  </div>
                </div>
              </div>

              <div className="profile-actions">
                <button onClick={() => { setEditing(false); fetchUserDetails(); }} className="btn-secondary">
                  Cancel
                </button>
                <button onClick={handleSave} className="btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          ) : (
            <div className="profile-info">
              <div className="profile-section">
                <h3>Account Information</h3>
                <div className="profile-field">
                  <label>Username:</label>
                  <span>{user?.username}</span>
                </div>
                <div className="profile-field">
                  <label>Role:</label>
                  <span className={`role-badge ${user?.role}`}>{user?.role}</span>
                </div>
                {userDetails?.expiration_date && (
                  <div className="profile-field">
                    <label>Mentorship Expires:</label>
                    <span>{formatDate(userDetails.expiration_date)}</span>
                  </div>
                )}
                {user?.created_by && (
                  <div className="profile-field">
                    <label>Created By:</label>
                    <span>{user.created_by}</span>
                  </div>
                )}
              </div>

              {(userDetails?.first_name || userDetails?.last_name || userDetails?.email || userDetails?.phone || userDetails?.date_of_birth || userDetails?.gender) && (
                <div className="profile-section">
                  <h3>Personal Information</h3>
                  {userDetails?.first_name && (
                    <div className="profile-field">
                      <label>First Name:</label>
                      <span>{userDetails.first_name}</span>
                    </div>
                  )}
                  {userDetails?.last_name && (
                    <div className="profile-field">
                      <label>Last Name:</label>
                      <span>{userDetails.last_name}</span>
                    </div>
                  )}
                  {userDetails?.email && (
                    <div className="profile-field">
                      <label>Email:</label>
                      <span>{userDetails.email}</span>
                    </div>
                  )}
                  {userDetails?.phone && (
                    <div className="profile-field">
                      <label>Phone:</label>
                      <span>{userDetails.phone}</span>
                    </div>
                  )}
                  {userDetails?.date_of_birth && (
                    <div className="profile-field">
                      <label>Date of Birth:</label>
                      <span>{formatDate(userDetails.date_of_birth)} {calculateAge(userDetails.date_of_birth) && `(${calculateAge(userDetails.date_of_birth)} years old)`}</span>
                    </div>
                  )}
                  {userDetails?.gender && (
                    <div className="profile-field">
                      <label>Gender:</label>
                      <span>{userDetails.gender}</span>
                    </div>
                  )}
                </div>
              )}

              {(userDetails?.height || userDetails?.weight || userDetails?.fitness_goals) && (
                <div className="profile-section">
                  <h3>Fitness Information</h3>
                  {userDetails?.height && (
                    <div className="profile-field">
                      <label>Height:</label>
                      <span>{userDetails.height} cm</span>
                    </div>
                  )}
                  {userDetails?.weight && (
                    <div className="profile-field">
                      <label>Weight:</label>
                      <span>{userDetails.weight} kg</span>
                    </div>
                  )}
                  {userDetails?.height && userDetails?.weight && (
                    <div className="profile-field">
                      <label>BMI:</label>
                      <span>{((userDetails.weight / ((userDetails.height / 100) ** 2))).toFixed(1)}</span>
                    </div>
                  )}
                  {userDetails?.fitness_goals && (
                    <div className="profile-field">
                      <label>Fitness Goals:</label>
                      <span className="fitness-goals-text">{userDetails.fitness_goals}</span>
                    </div>
                  )}
                </div>
              )}

              {!userDetails?.first_name && !userDetails?.last_name && !userDetails?.email && !userDetails?.phone && !userDetails?.height && !userDetails?.weight && (
                <div className="profile-empty">
                  <p>Your profile is empty. Click "Edit Profile" to add your information.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Profile;
